import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Button, Chip, Tooltip, TablePagination } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { interviewApi } from "../../api/interviewApi"
import CustomButton from "../../components/common/CustomButton";
import CustomHeader from "../../components/common/CustomHeader";
import CustomTable from "../../components/common/CustomTable";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomFilter from "../../components/common/CustomFilter";
import { flowwiseApi } from "../../api/flowiseApi";
import SkeletonLoader from "../../components/common/SkeletonLoader";



// Status Chip (UNCHANGED)
const getStatusChip = (status) => {
    if (!status) return null;

    const map = {
        completed: {
            bg: "#e6f4ea",
            color: "#137333",
            label: "Completed",
            tooltip: "Interview completed",
        },
        terminated: {
            bg: "#fdecea",
            color: "#b42318",
            label: "Terminated",
            tooltip: "Interview terminated",
        },
        interrupted: {
            bg: "#fff7e6",
            color: "#b26a00",
            label: "Interrupted",
            tooltip: "Interview interrupted",
        },
        scheduled: {
            bg: "#e8f0fe",
            color: "#1a4fd8",
            label: "Scheduled",
            tooltip: "Interview scheduled",
        },
    };

    const selected =
        map[status?.toLowerCase()] || {
            bg: "#f1f3f5",
            color: "#374151",
            label: status,
            tooltip: status,
        };

    return (
        <Tooltip title={selected.tooltip}>
            <Chip
                label={selected.label}
                size="small"
                clickable={false}
                sx={{
                    ml: 1,
                    height: "20px",
                    fontSize: "11px",
                    fontWeight: 600,
                    borderRadius: "6px",
                    bgcolor: selected.bg,
                    color: selected.color,
                    pointerEvents: "none",
                    cursor: "default"
                }}
            />
        </Tooltip>
    );
};

export const candidateFilters = [
    {
        key: "agg_score",
        label: "Score",
        type: "range",
        min: 0,
        max: 100,
        step: 1,
    },
    {
        key: "status",
        label: "Interview Status",
        type: "checkbox",
        options: [
            // { label: "Started", value: "STARTED" },
            // { label: "Scheduled", value: "SCHEDULED" },
            // { label: "In Progress", value: "IN PROGRESS" },
            { label: "Completed", value: "COMPLETED" },
            { label: "Interrupted", value: "INTERRUPTED" },
            { label: "Terminated", value: "TERMINATED" },
        ],
    }
];


const ALLOWED_STATUS = ["ATTENDED", "TERMINATED", "INTERRUPTED", "COMPLETED"];

const CandidateList = () => {
    const { state: job } = useLocation();
    const navigate = useNavigate();
    const [candidates, setCandidates] = useState([]);
    const [allCandidates, setAllCandidates] = useState([]); // 🔥 source of truth
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        type: "",
    });
    const [searchValue, setSearchValue] = useState("");

    const showSnackbar = (message, type) => {
        setSnackbar({ open: true, message, type });
    };

    const [filters, setFilters] = useState([{
        property: "jd_id",
        value: job.jd_id,
    }]);
    const values = useMemo(() => {
        const obj = {};

        filters.forEach(({ property, value, operator }) => {
            // RANGE (flatten it)
            if (operator) {
                obj[property] = {
                    value: value?.value ?? value, // 👈 FIX
                    operator,
                };
            }

            // MULTI
            else if (Array.isArray(obj[property])) {
                obj[property].push(value);
            }

            // FIRST TIME
            else if (!obj[property]) {
                obj[property] = [value];
            }

            // SAFETY
            else {
                obj[property] = [obj[property], value];
            }
        });

        return obj;
    }, [filters]);

    const handleChange = (key, value, mode, operator = "equal") => {
        if (key === "jd_id") return; // 🚫 prevent override

        setFilters((prev) => {
            let updatedFilters = [...prev];

            if (mode === "single") {
                updatedFilters = updatedFilters.filter(
                    (f) => f.property !== key
                );

                updatedFilters.push({
                    property: key,
                    value,
                    operator,
                });
            } else {
                const exists = updatedFilters.some(
                    (f) => f.property === key && f.value === value
                );

                if (exists) {
                    updatedFilters = updatedFilters.filter(
                        (f) =>
                            !(f.property === key && f.value === value)
                    );
                } else {
                    updatedFilters.push({
                        property: key,
                        value,
                    });
                }
            }

            // ✅ ALWAYS ensure jd_id exists
            const hasJdId = updatedFilters.some(
                (f) => f.property === "jd_id"
            );

            if (!hasJdId && job.jd_id) {
                updatedFilters.push({
                    property: "jd_id",
                    value: job.jd_id,
                });
            }

            return updatedFilters;
        });
    };

    const getCandidateResponse = async (apiCall) => {
        const response = await apiCall;
        const { status, message, data } = response || {};

        if (status === 0) {
            showSnackbar("🌐 Network error. Please check your internet connection.", "warning");
            return [];
        }

        if (status >= 500) {
            showSnackbar(message || "❌ Server error while fetching candidates.", "error");
            return [];
        }

        if (status >= 400) {
            showSnackbar(message || "⚠️ No candidates found for this job.", "info");
            return [];
        }

        if (status === 200 && data) {
            return data;
        }

        showSnackbar("⚠️ Unexpected response while fetching candidates.", "warning");
        return [];
    };
    const [loading, setLoading] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [total, setTotal] = useState(0);
    const [isFilterApplied, setIsFilterApplied] = useState(false);

    const fetchCandidates = async (job) => {
        setLoading(true);

        try {
            const api =
                job.mode === "basic"
                    ? interviewApi.getReport({ jd_id: job.jd_id })
                    : flowwiseApi.getReport({ jd_id: job.jd_id });

            const data = await getCandidateResponse(api);

            // ✅ show only required statuses
            const resultRows = data?.results || [];
            const filteredData = resultRows.filter((item) =>
                ALLOWED_STATUS.includes(
                    item?.interview_status?.toUpperCase()
                )
            );

            setTotal(data?.pagination?.total ?? 0);

            setCandidates(filteredData);
            setAllCandidates(filteredData);

        } catch (error) {
            console.error("Error fetching candidates:", error);
            showSnackbar(
                "❌ Something went wrong while loading candidates.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

   const handleApply = async () => {
    setPage(0);
    setIsFilterApplied(true);
    setLoading(true);

    try {
        const api =
            job.mode === "basic"
                ? interviewApi.getFilteredReport({
                    filters,
                    jd_id: job.jd_id,
                    page: page + 1,
                    page_size: rowsPerPage
                })
                : flowwiseApi.getFilteredReport(
                    {"filters": filters, "jd_id": job.jd_id},
                    page + 1,
                    rowsPerPage
                );

        const data = await getCandidateResponse(api);
        const resultRows = data?.results || [];

        // ✅ keep only required statuses
        const filteredData = resultRows.filter((item) =>
            ALLOWED_STATUS.includes(
                item?.interview_status?.toUpperCase()
            )
        );
        setTotal(
            data?.pagination?.total ?? 0
        );

            setCandidates(filteredData);

        } catch (error) {
            console.error("Error fetching candidates:", error);

            showSnackbar(
                "❌ Something went wrong while loading candidates.",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };
    const handleSearch = (value) => {
        setSearchValue(value);

        if (!value) {
            setCandidates(allCandidates); // ✅ restore original
            return;
        }

        const lower = value.toLowerCase();

        const filtered = allCandidates.filter((item) => {
            return (
                item.name?.toLowerCase().includes(lower) ||
                item.email?.toLowerCase().includes(lower) ||
                item.skills?.join(" ").toLowerCase().includes(lower)
            );
        });

        setCandidates(filtered);
    };

    const handleClear = async () => {
        setFilters([
            {
                property: "jd_id",
                value: job.jd_id,
            },
        ]);

        setSearchValue("");
        setPage(0);
        setIsFilterApplied(false);

        await fetchCandidates(job);
    };

    // Fetch Candidates
    useEffect(() => {
        if (!job) return;

        if (isFilterApplied) {
            handleApply();
        } else {
            fetchCandidates(job);
        }
    }, [job, page, rowsPerPage]);

    // Table Columns
    const columns = [
        {
            field: "full_name",
            label: "Candidate Name",
            headerSx: { textAlign: "left" },
            render: (value) => {
                if (!value) return "";
                return value
                    .toLowerCase()
                    .split(" ")
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ");
            }
        },

        {
            field: "agg_score",
            label: "Score",
            width: 150,
            headerAlign: "center",
            align: "center",
            render: (_, row) => {
                const qa = row.interview_QA || [];
                const avg = qa.length
                    ? qa.reduce((sum, q) => sum + (q.score || 0), 0) / qa.length
                    : 0;

                return `${avg.toFixed(2)}%`;
            },
            sx: {
                textAlign: "center",
            }
        },

        {
            field: "interview_status",
            label: "Interview Status",
            width: 150,
            headerAlign: "center",
            align: "center",
            render: (value) => (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {getStatusChip(value.toLowerCase())}
                </Box>
            ),
            sx: {
                textAlign: "center",
            }
        },
        {
            field: "cheating_status",
            label: "Cheating Status",
            width: 150,
            headerAlign: "center",

            align: "center",
            render: (value) => (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    {getStatusChip(value)}
                </Box>
            ),
            sx: {
                textAlign: "center",
            }
        },
        {
            field: "actions",
            label: "Report",
            align: "center",
            headerAlign: "center",

            render: (_, row) => (
                <Box sx={{ textAlign: "center" }}>
                    <CustomButton
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={() =>
                            navigate(`/reports/candidates/detail`, {
                                state: { report: row },
                            })
                        }
                    >
                        View →
                    </CustomButton>
                </Box>
            ),
            sx: {
                textAlign: "center",
                width: "150px",
            }
        },
    ];


    return (
        <Box
            sx={{
                minHeight: "90vh",
            }}
        >
            <CustomHeader
                title="Interview Reports"
            />
            <Box sx={{ p: 1 }}>
                <CustomFilter
                    filtersConfig={candidateFilters}
                    values={values}
                    onChange={handleChange}
                    onApply={handleApply}
                    onClear={handleClear}
                    onSearch={handleSearch}
                    searchValue={searchValue}
                />
               {loading ? (
    <SkeletonLoader
        type="table"
        rows={6}
        columns={columns}
    />
) : candidates.length > 0 ? (
    <>
        <CustomTable
            columns={columns}
            rows={candidates}
        />
        <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            onPageChange={(_, newPage) => {
                setPage(newPage);
            }}
            onRowsPerPageChange={(e) => {
                setRowsPerPage(
                    parseInt(e.target.value, 10)
                );
                setPage(0);
            }}
        />
    </>
) : (
    <Box
        sx={{
            height: 250,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }}
    >
        <Typography
            variant="body1"
            color="text.secondary"
        >
            No candidates found
        </Typography>
    </Box>
)}
                
            </Box>

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </Box>
    );
};

export default CandidateList;
